import type { JsonLd } from './HeadDescriptor.js';
import { absolutizar } from './absolutizar.js';

/**
 * Builders de structured data (schema.org) para el portal.
 *
 * Google usa `NewsArticle` para Google News / Discover y los rich results de noticias:
 * es parte del andamiaje SEO, no un extra. Acá quedan las firmas y la forma del JSON;
 * los datos reales los va a pasar cada loader cuando existan las páginas.
 */

export interface SiteInfo {
  name: string;
  /** URL absoluta del sitio, sin barra final (ej: https://ahorapatagonia.com). */
  url: string;
  logoUrl?: string;
}

export interface ArticleInfo {
  title: string;
  description?: string;
  url: string;
  imageUrl?: string;
  publishedAt?: string;
  updatedAt?: string;
  authorName?: string;
  section?: string;
}

export function organizationJsonLd(site: SiteInfo): JsonLd {
  // El logo va absoluto: es `publisher` de cada `NewsArticle` y lo consume Google, no un browser
  // parado en el sitio.
  const logo = absolutizar(site.url, site.logoUrl);
  return {
    '@context': 'https://schema.org',
    '@type': 'NewsMediaOrganization',
    name: site.name,
    url: site.url,
    ...(logo ? { logo: { '@type': 'ImageObject', url: logo } } : {}),
  };
}

export function websiteJsonLd(site: SiteInfo): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: site.name,
    url: site.url,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${site.url}/buscar?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

export function newsArticleJsonLd(article: ArticleInfo, site: SiteInfo): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: article.title,
    ...(article.description ? { description: article.description } : {}),
    mainEntityOfPage: { '@type': 'WebPage', '@id': article.url },
    ...(article.imageUrl ? { image: [article.imageUrl] } : {}),
    ...(article.publishedAt ? { datePublished: article.publishedAt } : {}),
    ...(article.updatedAt ? { dateModified: article.updatedAt } : {}),
    ...(article.authorName
      ? { author: { '@type': 'Person', name: article.authorName } }
      : {}),
    ...(article.section ? { articleSection: article.section } : {}),
    publisher: organizationJsonLd(site),
  };
}

export function breadcrumbJsonLd(items: Array<{ name: string; url: string }>): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}
